/**
 * Ledger / Reconciler — super-admin console page.
 *
 * Thin console over three existing endpoints (nothing money-code is
 * changed here):
 *   GET  /api/admin/economy/reconcile/latest   — snapshot for the view
 *   POST /api/admin/economy/reconcile          — "Run Now"
 *   POST /api/admin/accounts/purge_non_real    — dry-run + real delete
 *
 * The purge endpoint is HARD-SCOPED server-side to `test` / `qa` only.
 * Real, bot, internal, and official accounts CANNOT be purged from
 * this page or anywhere else — the UI shows that guarantee prominently.
 *
 * No gambling terminology anywhere in the copy.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  Activity,
  RefreshCw,
  Play,
  AlertTriangle,
  ShieldAlert,
  Lock,
  Trash2,
  ShieldCheck,
  CheckCircle2,
} from 'lucide-react';


const _fmt = (n) => new Intl.NumberFormat().format(Math.round(Number(n) || 0));

const SnapshotHeader = ({ snapshot }) => {
  if (!snapshot) return null;
  if (!snapshot.has_run) {
    return (
      <div
        className="rounded-xl border border-gray-200 bg-white p-5"
        data-testid="reconciler-snapshot-empty"
      >
        <div className="text-sm text-gray-600">
          {snapshot.message || 'No snapshot yet.'}
        </div>
      </div>
    );
  }
  const when = snapshot.finished_at
    ? new Date(snapshot.finished_at).toLocaleString()
    : '—';
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex items-center flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500">
            Latest reconciliation
          </div>
          <div className="text-lg font-semibold text-gray-900">{when}</div>
          <div className="text-xs text-gray-500 mt-0.5">
            triggered by {snapshot.triggered_by || 'scheduler'} · run{' '}
            <code className="text-[10px] bg-gray-100 rounded px-1">
              {(snapshot.run_id || '').slice(0, 8)}
            </code>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {snapshot.calibration ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
              <ShieldCheck className="w-3.5 h-3.5" /> Baseline (calibration)
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
              <Activity className="w-3.5 h-3.5" /> Live run
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500">Users checked</div>
          <div className="text-xl font-semibold text-gray-900">
            {_fmt(snapshot.scanned_users)}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500">Flagged</div>
          <div className="text-xl font-semibold text-gray-900">
            {_fmt(snapshot.drift_summary?.flagged_users)}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500">Total |drift| rubies</div>
          <div className="text-xl font-semibold text-gray-900">
            {_fmt(snapshot.drift_summary?.total_abs_drift)}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500">Tolerance</div>
          <div className="text-xl font-semibold text-gray-900">
            ±{snapshot.tolerance ?? 0}
          </div>
        </div>
      </div>
    </div>
  );
};


const TopFlaggedTable = ({ rows }) => {
  if (!rows?.length) {
    return (
      <div
        className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-800 flex items-center gap-3"
        data-testid="reconciler-clean-state"
      >
        <CheckCircle2 className="w-5 h-5" />
        <div>
          <div className="font-semibold">No drift flagged in the latest run.</div>
          <div className="text-sm text-emerald-700 mt-0.5">
            The ruby ledger balances every user we checked.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
          <tr>
            <th className="px-4 py-2 text-left">Handle</th>
            <th className="px-4 py-2 text-right">Drift</th>
            <th className="px-4 py-2 text-right">Expected</th>
            <th className="px-4 py-2 text-right">Actual</th>
            <th className="px-4 py-2 text-left">Flags</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r) => (
            <tr key={r.user_id} className="hover:bg-gray-50">
              <td className="px-4 py-2">
                <div className="font-medium text-gray-900">
                  {r.handle || r.name || r.user_id.slice(0, 8)}
                </div>
                <div className="text-xs text-gray-500">
                  {r.user_id.slice(0, 12)}
                </div>
              </td>
              <td
                className={`px-4 py-2 text-right font-mono ${
                  r.drift > 0
                    ? 'text-amber-700'
                    : r.drift < 0
                    ? 'text-rose-700'
                    : 'text-gray-700'
                }`}
              >
                {r.drift > 0 ? '+' : ''}
                {_fmt(r.drift)}
              </td>
              <td className="px-4 py-2 text-right font-mono text-gray-700">
                {_fmt(r.expected_rubies)}
              </td>
              <td className="px-4 py-2 text-right font-mono text-gray-700">
                {_fmt(r.actual_rubies)}
              </td>
              <td className="px-4 py-2">
                <div className="flex flex-wrap gap-1">
                  {(r.flags || []).map((f) => (
                    <span
                      key={f}
                      className={`text-xs px-2 py-0.5 rounded-full border ${
                        f === 'negative_balance'
                          ? 'bg-rose-50 text-rose-700 border-rose-200'
                          : f === 'balance_without_ledger_history'
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-gray-50 text-gray-600 border-gray-200'
                      }`}
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


const ReconcilerPage = () => {
  const { api, admin } = useAdmin();
  const isSuperAdmin = admin?.role === 'super_admin';

  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  // Purge
  const [purgeDryRun, setPurgeDryRun] = useState(null);
  const [purgeChecking, setPurgeChecking] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [purgeExecuting, setPurgeExecuting] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);

  // -------- Load snapshot -------------------------------------------
  const loadSnapshot = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/admin/economy/reconcile/latest');
      setSnapshot(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load snapshot');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (isSuperAdmin) loadSnapshot();
  }, [isSuperAdmin]);

  // -------- Run Now --------------------------------------------------
  const handleRunNow = async () => {
    setRunning(true);
    try {
      const r = await api.post('/api/admin/economy/reconcile');
      const s = r.data?.summary;
      toast.success(
        `Reconciler run complete — scanned ${_fmt(s?.scanned_users)} users, ${_fmt(
          s?.flagged_count
        )} flagged`
      );
      await loadSnapshot();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reconciler failed');
    } finally {
      setRunning(false);
    }
  };

  // -------- Purge dry-run -------------------------------------------
  const handlePurgeDryRun = async () => {
    setPurgeChecking(true);
    setPurgeResult(null);
    setPurgeConfirm('');
    try {
      const r = await api.post('/api/admin/accounts/purge_non_real?dry_run=true');
      setPurgeDryRun(r.data);
      toast.success(
        `${r.data.total_matched} account${
          r.data.total_matched === 1 ? '' : 's'
        } match the test/QA scope`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purge check failed');
    } finally {
      setPurgeChecking(false);
    }
  };

  // -------- Purge execute (typed confirm) ---------------------------
  const purgeTotal = purgeDryRun?.total_matched ?? 0;
  const canExecutePurge = useMemo(() => {
    if (!purgeDryRun || purgeTotal <= 0) return false;
    const t = purgeConfirm.trim();
    // Either the exact count OR the literal string PURGE.
    return t === String(purgeTotal) || t === 'PURGE';
  }, [purgeConfirm, purgeTotal, purgeDryRun]);

  const handlePurgeExecute = async () => {
    if (!canExecutePurge) return;
    const ok = window.confirm(
      `Delete ${purgeTotal} test/QA account${
        purgeTotal === 1 ? '' : 's'
      }? This is not reversible.\n\nReal, bot, internal and official accounts CANNOT be affected — the server enforces the scope.`
    );
    if (!ok) return;
    setPurgeExecuting(true);
    try {
      const r = await api.post('/api/admin/accounts/purge_non_real?dry_run=false');
      setPurgeResult(r.data);
      setPurgeDryRun(null);
      setPurgeConfirm('');
      toast.success(
        `Purged ${r.data.deleted_count} account${
          r.data.deleted_count === 1 ? '' : 's'
        }`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purge failed');
    } finally {
      setPurgeExecuting(false);
    }
  };

  // -------- Access guard --------------------------------------------
  if (!isSuperAdmin) {
    return (
      <div
        data-testid="reconciler-access-denied"
        className="max-w-2xl mx-auto mt-16 bg-white border border-gray-200 rounded-2xl p-8 text-center"
      >
        <Lock className="w-12 h-12 mx-auto text-rose-500 mb-3" />
        <h2 className="text-xl font-bold text-gray-900 mb-1">Super-admin only</h2>
        <p className="text-gray-600 text-sm">
          The Ledger / Reconciler console can only be used by super-admins.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reconciler-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-emerald-600" />
            Ledger &amp; Reconciler
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Read-only integrity check of the ruby ledger. Runs nightly; you can trigger a fresh pass any time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadSnapshot}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"
            title="Refresh"
            data-testid="reconciler-refresh"
          >
            <RefreshCw className={`w-4 h-4 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleRunNow}
            disabled={running}
            data-testid="reconciler-run-now"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
          >
            <Play className="w-4 h-4" />
            {running ? 'Running…' : 'Run Now'}
          </button>
        </div>
      </div>

      {/* Snapshot */}
      <div data-testid="reconciler-snapshot" className="space-y-4">
        <SnapshotHeader snapshot={snapshot} />
        {snapshot?.has_run && (
          <>
            <div className="text-sm text-gray-600 font-medium">
              Top {snapshot.top_flagged?.length || 0} flagged users
            </div>
            <TopFlaggedTable rows={snapshot.top_flagged || []} />
          </>
        )}
      </div>

      {/* Purge Test/QA */}
      <div className="rounded-2xl border border-rose-200 bg-white p-5 space-y-4">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-6 h-6 text-rose-500 shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Purge Test / QA accounts
            </h2>
            <p className="text-sm text-gray-600 mt-0.5">
              Server-side scope is <strong>hard-locked to <code>test</code> and <code>qa</code></strong> accounts.
              Real, bot, internal, and official accounts CANNOT be affected — the filter is
              enforced with a defence-in-depth assertion in <code>purge_non_real_accounts</code>.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handlePurgeDryRun}
            disabled={purgeChecking}
            data-testid="purge-dryrun-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-60"
          >
            {purgeChecking ? 'Checking…' : 'Check test/QA accounts'}
          </button>
          {purgeDryRun && (
            <div className="text-sm text-gray-700">
              <strong>{purgeDryRun.total_matched}</strong> matched ·{' '}
              {Object.entries(purgeDryRun.by_type || {}).length === 0
                ? 'none'
                : Object.entries(purgeDryRun.by_type).map(([k, v]) => (
                    <span key={k} className="mr-2">
                      {k}: <strong>{v}</strong>
                    </span>
                  ))}
            </div>
          )}
        </div>

        {purgeDryRun && purgeDryRun.total_matched > 0 && (
          <div className="border-t border-gray-100 pt-4 space-y-3">
            <div className="text-sm text-gray-700">
              Type the exact count (<code>{purgeDryRun.total_matched}</code>) or the word{' '}
              <code>PURGE</code> to unlock the delete button.
            </div>
            <input
              type="text"
              value={purgeConfirm}
              onChange={(e) => setPurgeConfirm(e.target.value)}
              placeholder={`Type ${purgeDryRun.total_matched} or PURGE`}
              data-testid="purge-confirm-input"
              className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            />
            <div>
              <button
                type="button"
                onClick={handlePurgeExecute}
                disabled={!canExecutePurge || purgeExecuting}
                data-testid="purge-execute-btn"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium ${
                  canExecutePurge
                    ? 'bg-rose-600 hover:bg-rose-700'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                <Trash2 className="w-4 h-4" />
                {purgeExecuting
                  ? 'Purging…'
                  : `Purge ${purgeDryRun.total_matched} account${
                      purgeDryRun.total_matched === 1 ? '' : 's'
                    }`}
              </button>
            </div>
          </div>
        )}

        {purgeResult && (
          <div
            className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 flex items-start gap-2"
            data-testid="purge-result"
          >
            <CheckCircle2 className="w-4 h-4 mt-0.5" />
            <span>
              Deleted <strong>{purgeResult.deleted_count}</strong> account
              {purgeResult.deleted_count === 1 ? '' : 's'} · scope:{' '}
              {(purgeResult.purgeable_types || []).join(', ')} · by-type{' '}
              {JSON.stringify(purgeResult.by_type || {})}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReconcilerPage;
